// Feature RBAC model (frontend) — Licenses → Bundles → Users.
//
//   FEATURES  atomic capabilities (registry, mirrors backend config/features.js)
//   BUNDLE    a named set of features (editable); the unit assigned to users
//   PLAN      a purchasable plan (today's `products`); source for creating licenses
//   LICENSE   a company's instance of a plan: seat_count (admin) + offered bundles
//   ASSIGNMENT a user occupies a seat in a license and holds bundle(s) from its menu
//   OVERRIDE  per-user allow/deny of individual features (exceptions)
//
// Effective features for a user (live):
//   GH Admin ? ALL : ( ∪ features of assigned bundles ) ∪ allows \ denies
//
// This module is the static registry ONLY — the feature list, the bundle
// catalog and its *default* feature sets, and the plan→bundle mapping.
//
// A bundle's REAL feature set can differ: a gohire-admin can customize it, and
// that override lives in the `bundle_features` table. So anything rendering
// "what does this bundle grant" must resolve through ./useBundleFeatures (which
// reads GET /api/v3/bundles), NOT through the defaults here. The helpers below
// are named `default*` for that reason — they are the pre-load fallback.

import appConfig from '../../config';

// ── Feature registry ─────────────────────────────────────────────────────────
export const FEATURES = [
    { key: 'inbox', label: 'Inbox / conversations', group: 'Messaging', color: 'blue' },
    { key: 'messaging.send', label: 'Send messages (1-1 & reply)', group: 'Messaging', color: 'geekblue' },
    { key: 'contacts', label: 'Contacts & Groups', group: 'Messaging', color: 'cyan' },
    { key: 'templates.use', label: 'Use templates', group: 'Templates', color: 'green' },
    { key: 'templates.manage', label: 'Create / share templates', group: 'Templates', color: 'lime' },
    { key: 'broadcasts', label: 'Broadcasts / campaigns', group: 'Outreach', color: 'volcano' },
    { key: 'interview-scheduling', label: 'Interview invites & scheduling', group: 'Recruiting', color: 'gold' },
    { key: 'scheduling-manager', label: "Book on a teammate's calendar", group: 'Recruiting', color: 'gold' },
    { key: 'apply-by-text', label: 'Apply-by-Text', group: 'Recruiting', color: 'orange' },
    // scope 'company' (ADR-033): AI features are company-wide — toggled on the Companies page AI card,
    // granted to every user of an entitled company, never a per-user override, never a seat.
    { key: 'ai-assistant', label: 'AI Assistant', group: 'AI', color: 'purple', scope: 'company' },
    { key: 'ai-intent-fallback', label: 'AI reply understanding', group: 'AI', color: 'purple', scope: 'company' },
    // ADR-035: inbound shown in the recruiter's language, replies (human + bot) sent in the candidate's.
    { key: 'ai-translation', label: 'AI translation', group: 'AI', color: 'purple', scope: 'company' },
    { key: 'webchat', label: 'Webchat widget', group: 'Channels', color: 'magenta' },
    { key: 'integrations', label: 'Integrations (ATS/HRIS)', group: 'Platform', color: 'geekblue' },
    { key: 'reporting', label: 'Reporting & analytics', group: 'Platform', color: 'cyan' },
];
export const FEATURE_KEYS = FEATURES.map((f) => f.key);
export const FEATURE_BY_KEY = FEATURES.reduce((a, f) => { a[f.key] = f; return a; }, {});

// Upsell copy shown on the locked/paywall overlay when a user lacks a feature.
// Mirrored in the Chrome extension (src/utils/features.ts) — keep both in sync.
export const FEATURE_UPSELL = {
    inbox: { title: 'Inbox isn’t in your plan', blurb: 'Manage two-way candidate conversations in one shared inbox.' },
    'messaging.send': { title: 'Messaging isn’t in your plan', blurb: 'Text candidates 1-to-1 and reply in real time.' },
    contacts: { title: 'Contacts isn’t in your plan', blurb: 'Store, tag and group your candidate contacts.' },
    'templates.use': { title: 'Templates aren’t in your plan', blurb: 'Send faster with reusable message templates.' },
    'templates.manage': { title: 'Template management isn’t in your plan', blurb: 'Create and share message templates across your team.' },
    broadcasts: { title: 'Broadcasts isn’t in your plan', blurb: 'Send bulk SMS campaigns to contact groups and reach candidates at scale.' },
    'interview-scheduling': { title: 'Interview Scheduling isn’t in your plan', blurb: 'Let candidates book interviews and send calendar invites automatically.' },
    'scheduling-manager': { title: 'Scheduling Manager isn’t in your plan', blurb: 'Send interview invites that book onto any teammate’s calendar — with live availability before you send.' },
    'apply-by-text': { title: 'Apply-by-Text isn’t in your plan', blurb: 'Turn any job posting into a text-to-apply candidate funnel.' },
    'ai-assistant': { title: 'AI Assistant isn’t in your plan', blurb: 'Automate screening and replies with an AI recruiting assistant.' },
    'ai-intent-fallback': { title: 'AI reply understanding isn’t in your plan', blurb: 'Let the bot understand candidates who reply in their own words — “wed 10:45”, “yes I’d love to” — instead of asking them to try again.' },
    'ai-translation': { title: 'AI translation isn’t in your plan', blurb: 'Read every candidate in your language and reply in theirs — inbound texts are translated as they arrive, and your replies and bot messages go out in the candidate’s language.' },
    webchat: { title: 'Webchat isn’t in your plan', blurb: 'Capture and message candidates from a widget on your site.' },
    integrations: { title: 'Integrations aren’t in your plan', blurb: 'Sync candidates with your ATS / HRIS automatically.' },
    reporting: { title: 'Reporting isn’t in your plan', blurb: 'Track messaging, scheduling and campaign performance.' },
};
export const FEATURE_GROUPS = FEATURES.reduce((a, f) => { (a[f.group] = a[f.group] || []).push(f); return a; }, {});

// UI-only metadata for AI-group features on the Companies page's AI card (ADR-033):
// where an admin goes to manage the feature's own configuration.
export const AI_FEATURE_META = {
    'ai-intent-fallback': { manage: { label: 'Dictionary overrides', to: (companyId) => `/intent-dictionary?company=${companyId}` } },
    'ai-assistant': { manage: { label: 'Assistants', to: () => '/ai-assistants' } },
    // ADR-035 — per-company knobs stored in company_ai_feature_settings.settings and
    // edited inline on the AI card (PUT …/ai-features/ai-translation { settings }).
    'ai-translation': {
        settingsFields: [
            {
                key: 'base_language',
                label: 'Flow language',
                type: 'language',
                default: 'en',
                help: 'The language your bot flows are written in. Candidate replies are understood against it, and bot messages are translated FROM it.',
            },
            {
                key: 'translate_bot_replies',
                label: 'Translate bot replies',
                type: 'switch',
                default: true,
                help: 'Bot flow messages go out in the candidate’s language when it differs from the flow language. Hand-built bilingual flows are left alone (already-translated text is detected and skipped).',
            },
        ],
    },
};

// Put an arbitrary set of feature keys into stable registry order.
export function orderFeatureKeys(keys) {
    const s = keys instanceof Set ? keys : new Set(keys);
    return FEATURE_KEYS.filter((k) => s.has(k));
}

// ── Bundle catalog (named feature sets; DEFAULTS — see header) ────────────────
export const BUNDLES = [
    { key: 'messaging', label: 'Texting', features: ['inbox', 'messaging.send', 'contacts', 'templates.use', 'templates.manage'] },
    { key: 'scheduling', label: 'Interview Scheduling', features: ['interview-scheduling'] },
    { key: 'scheduling-manager', label: 'Scheduling Manager', features: ['scheduling-manager'] },
    { key: 'broadcasts', label: 'Broadcasts', features: ['broadcasts'] },
    { key: 'apply-by-text', label: 'Apply-by-Text', features: ['apply-by-text', 'inbox', 'messaging.send', 'contacts'] },
    { key: 'webchat', label: 'Webchat', features: ['webchat'] },
    // scope 'company': sits on a license but is toggled from the Companies page AI card, never
    // assigned to a user, never a seat. Hidden from per-user pickers.
    { key: 'ai', label: 'AI Assistant', features: ['ai-assistant'], scope: 'company' },
    { key: 'ai-intent', label: 'AI Reply Understanding', features: ['ai-intent-fallback'], scope: 'company' },
    { key: 'ai-translation', label: 'AI Translation', features: ['ai-translation'], scope: 'company' },
    { key: 'integrations', label: 'Integrations', features: ['integrations'] },
    { key: 'reporting', label: 'Reporting', features: ['reporting'] },
];
export const BUNDLE_KEYS = BUNDLES.map((b) => b.key);
export const BUNDLE_BY_KEY = BUNDLES.reduce((a, b) => { a[b.key] = b; return a; }, {});
export const isCompanyBundle = (key) => !!(BUNDLE_BY_KEY[key] && BUNDLE_BY_KEY[key].scope === 'company');
export const isCompanyFeature = (key) => !!(FEATURE_BY_KEY[key] && FEATURE_BY_KEY[key].scope === 'company');

// A bundle's CONFIG DEFAULT features — used by the bundle editor to show
// "default vs customized", and as useBundleFeatures' pre-load fallback.
export function defaultBundleFeatures(bundleKey) {
    const b = BUNDLE_BY_KEY[bundleKey];
    return b ? orderFeatureKeys(b.features) : [];
}

// Union of the DEFAULT features of a set of bundles. Not the live answer —
// see the header; use useBundleFeatures().featuresForBundles for that.
export function defaultFeaturesForBundles(bundleKeys) {
    const s = new Set();
    (bundleKeys || []).forEach((k) => defaultBundleFeatures(k).forEach((f) => s.add(f)));
    return orderFeatureKeys(s);
}

const orderBundles = (keys) => {
    const s = keys instanceof Set ? keys : new Set(keys);
    return BUNDLE_KEYS.filter((k) => s.has(k));
};

// ── Plan catalog → default offered bundles ───────────────────────────────────
// A plan (product) implies bundles via its `services` (backend enum 1-6).
export const SERVICE_BUNDLES = {
    1: ['webchat'],
    2: ['messaging'], // twilio
    3: ['scheduling'],
    4: ['messaging'], // nexmo
    5: ['broadcasts'],
    6: ['apply-by-text'],
};
export function parseServices(raw) {
    if (Array.isArray(raw)) return raw.map(Number);
    if (typeof raw === 'number') return [raw];
    try { const p = JSON.parse(raw || '[]'); return Array.isArray(p) ? p.map(Number) : []; } catch { return []; }
}
export function defaultBundlesForPlan(product) {
    const s = new Set();
    parseServices(product && product.services).forEach((svc) => (SERVICE_BUNDLES[svc] || []).forEach((b) => s.add(b)));
    return orderBundles(s);
}

// ── Effective resolution ─────────────────────────────────────────────────────
// Local preview used by the Access editor while the admin is still editing (the
// saved answer comes from the backend). `resolveBundles` MUST be the live
// resolver from useBundleFeatures — otherwise customizations are ignored.
export function effectiveFeatures({ assignments = [], allows = [], denies = [] }, resolveBundles) {
    const bundleKeys = new Set();
    assignments.forEach((a) => (a.bundleKeys || []).forEach((k) => bundleKeys.add(k)));
    const resolve = resolveBundles || defaultFeaturesForBundles;
    const s = new Set([...resolve([...bundleKeys]), ...allows]);
    denies.forEach((d) => s.delete(d));
    return orderFeatureKeys(s);
}

// GH Admins bypass all feature controls — they always have every feature.
export const isGhAdminUser = (user) => (user && (user.role || '')) === appConfig.userRoles.ghAdmin;
